import React from 'react';
import { 
    Box, 
    TextField, 
    Typography, 
    FormControlLabel,
    Checkbox
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import styled from "@emotion/styled";
import { TrackedButton } from '../TrackedButton';
import { ComposePostEvents } from '../../services/analytics/events';
import { getPlatformIconJSX } from '../../utils/platformUtils';
import TikTokPostContainer from '../TikTokPostContainer';

const DEFAULT_PLATFORM_LIMITS = {
    twitter: 280,
    facebook: 2200,
    instagram: 2200,
    linkedin: 3000,
    bluesky: 300,
    tiktok: 4000,
    threads: 500
};

const PostBox = styled.div(({theme, disabled}) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1),
    },
    ...(disabled && {
        backgroundColor: theme.palette.action.disabledBackground,
        opacity: 0.7,
    })
}));

const CharacterWarning = styled(Typography)`
    color: ${props => props.theme.palette.error.main};
    text-align: right;
    font-size: 0.875rem;
    margin-top: 4px;
`;

const StyledAvatar = styled(Avatar)(({ theme, selected, isGrayscale }) => ({
    width: 40,
    height: 40,
    filter: isGrayscale ? 'grayscale(100%)' : 'none',
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',
    ...(selected && {
        border: `3px solid ${theme.palette.success.main}`,
        boxSizing: 'border-box',
        transform: 'scale(1.05)',
    }),
}));

const AvatarWrapper = styled.div`
    position: relative;
    display: inline-block;
    transition: all 0.2s ease-in-out;
`;

const IconOverlay = styled.div`
    position: absolute;
    bottom: -5px;
    right: -5px;
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
`;

// Platform icon helper function is now imported from utils

/**
 * PlatformInputs Component
 * Handles text input for each selected platform with character validation
 * Following PostDetailsModal.js clean structure patterns
 */
const PlatformInputs = ({
    selectedAccounts,
    useCommonText,
    commonText,
    onCommonTextChange,
    onToggleCommonText,
    onInputChange,
    onClearAll,
    isSubmitting,
    platformCapabilities, // Enhanced Platform Service capabilities
    // TikTok specific props
    onTikTokPostComplete,
    onTikTokError,
    onTikTokFormValidityChange,
    tiktokFormValidity
}) => {
    const getCharacterCount = (text) => {
        return text ? text.length : 0;
    };

    const getPlatformLimit = (platform) => {
        if (platformCapabilities && platformCapabilities[platform]) {
            return platformCapabilities[platform].maxLength || DEFAULT_PLATFORM_LIMITS[platform.toLowerCase()];
        }
        return DEFAULT_PLATFORM_LIMITS[platform.toLowerCase()] || 2200; // Default fallback
    };

    const hasCharacterLimitExceeded = () => {
        return selectedAccounts.some(account =>
            getCharacterCount(account.message) > getPlatformLimit(account.platform)
        );
    };

    if (selectedAccounts.length === 0) {
        return (
            <PostBox>
                <Typography align="center" variant="body2">
                    Please select an account
                </Typography>
            </PostBox>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {/* Common Text Controls */}
            {selectedAccounts.length >= 2 && (
                <Box display="flex" justifyContent="space-between" sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={useCommonText}
                                onChange={(e) => onToggleCommonText(e.target.checked)}
                            />
                        }
                        label="Use same text across all platforms"
                    />
                    <TrackedButton
                        variant="outlined"
                        onClick={onClearAll}
                        trackingData={ComposePostEvents.POST_CLEAR_ALL_CLICKED}
                        disabled={
                            !selectedAccounts.some(account => account.message) && 
                            !commonText
                        }
                        sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 1, sm: 0 } }}
                    >
                        Clear All Text
                    </TrackedButton>
                </Box>
            )}

            {/* Platform Input Boxes */}
            <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                maxHeight: '50vh',
                overflowY: 'auto'
            }}>
                {selectedAccounts.map((account, index) => (
                    <PostBox
                        key={account.accountId}
                        disabled={useCommonText && index !== 0}
                    >
                        {/* Platform Header */}
                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                            <AvatarWrapper>
                                <StyledAvatar
                                    alt={account.username}
                                    selected={true}
                                    src={account.account_img}
                                />
                                <IconOverlay>
                                    {getPlatformIconJSX(account.platform)}
                                </IconOverlay>
                            </AvatarWrapper>
                            <Typography variant="subtitle1">
                                {account.username} ({account.platform})
                            </Typography>
                        </Box>

                        {/* Text Input */}
                        <TextField
                            multiline
                            rows={4}
                            variant="outlined"
                            fullWidth
                            disabled={useCommonText && index !== 0}
                            value={
                                useCommonText 
                                    ? (index === 0 ? commonText : selectedAccounts[0].message) 
                                    : account.message
                            }
                            onChange={
                                useCommonText && index === 0 
                                    ? onCommonTextChange 
                                    : (e) => onInputChange(e, account.accountId)
                            }
                            placeholder={`What's on your mind for ${account.platform}?`}
                            inputProps={{
                                maxLength: getPlatformLimit(account.platform) + 100, // Allow slight overflow for warning
                                'aria-label': `Post content for ${account.username} on ${account.platform}`
                            }}
                        />

                        {/* Character Count Warning */}
                        {getCharacterCount(account.message) > 
                         getPlatformLimit(account.platform) - 20 && (
                            <CharacterWarning>
                                {getCharacterCount(account.message)}/
                                {getPlatformLimit(account.platform)} characters
                                {getCharacterCount(account.message) > 
                                 getPlatformLimit(account.platform) &&
                                    ' - Text exceeds limit!'
                                }
                            </CharacterWarning>
                        )}

                        {/* TikTok Specific Container */}
                        {account.platform === 'tiktok' && (
                            <TikTokPostContainer
                                socialAccountId={account.accountId}
                                message={account.message}
                                onMessageChange={(e) => onInputChange(e, account.accountId)}
                                onPostComplete={onTikTokPostComplete}
                                onError={onTikTokError}
                                onFormValidityChange={onTikTokFormValidityChange}
                            />
                        )}
                    </PostBox>
                ))}
            </Box>

            {/* Validation Summary */}
            {hasCharacterLimitExceeded() && (
                <Box 
                    role="alert"
                    sx={{
                        backgroundColor: 'error.light',
                        color: 'error.contrastText',
                        p: 2,
                        mt: 2,
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'error.main',
                    }}
                >
                    <Typography variant="body2" fontWeight="medium">
                        ⚠️ Some posts exceed character limits. Please review and shorten the content.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default PlatformInputs; 